// Permissions Matrix for the Flexible Role System
// This file defines what actions each capability allows

// Define all available capabilities
export const CAPABILITIES = {
  CAN_APPLY_FOR_JOBS: 'canApplyForJobs',
  CAN_BOOK_SERVICES: 'canBookServices', 
  CAN_PROVIDE_SERVICES: 'canProvideServices',
  CAN_POST_JOBS: 'canPostJobs'
};

// Define all available actions
export const ACTIONS = {
  // Job-related actions
  BROWSE_JOBS: 'browseJobs',
  APPLY_FOR_JOBS: 'applyForJobs',
  VIEW_JOB_APPLICATIONS: 'viewJobApplications',
  POST_JOBS: 'postJobs',
  MANAGE_JOB_POSTINGS: 'manageJobPostings',
  VIEW_JOB_APPLICANTS: 'viewJobApplicants',
  APPROVE_JOB_APPLICATIONS: 'approveJobApplications',
  
  // Service-related actions
  BROWSE_SERVICES: 'browseServices',
  BOOK_SERVICES: 'bookServices',
  POST_SERVICES: 'postServices',
  MANAGE_SERVICE_LISTINGS: 'manageServiceListings',
  APPROVE_SERVICE_BOOKINGS: 'approveServiceBookings',
  VIEW_SERVICE_BOOKINGS: 'viewServiceBookings',
  
  // Profile and account actions
  MANAGE_PROFILE: 'manageProfile',
  VIEW_ANALYTICS: 'viewAnalytics',
  MANAGE_USERS: 'manageUsers',
  
  // Admin actions
  APPROVE_JOBS_SERVICES: 'approveJobsServices',
  MANAGE_PLATFORM: 'managePlatform'
};

// Permissions Matrix - defines which capabilities allow which actions
export const PERMISSIONS_MATRIX = {
  // Job Seeker Capabilities
  [CAPABILITIES.CAN_APPLY_FOR_JOBS]: [
    ACTIONS.BROWSE_JOBS,
    ACTIONS.APPLY_FOR_JOBS,
    ACTIONS.VIEW_JOB_APPLICATIONS,
    ACTIONS.MANAGE_PROFILE
  ],
  
  [CAPABILITIES.CAN_BOOK_SERVICES]: [
    ACTIONS.BROWSE_SERVICES,
    ACTIONS.BOOK_SERVICES,
    ACTIONS.VIEW_SERVICE_BOOKINGS,
    ACTIONS.MANAGE_PROFILE
  ],
  
  // Service Provider Capabilities
  [CAPABILITIES.CAN_PROVIDE_SERVICES]: [
    ACTIONS.BROWSE_SERVICES,
    ACTIONS.BOOK_SERVICES, // Service providers can also book services from others
    ACTIONS.POST_SERVICES,
    ACTIONS.MANAGE_SERVICE_LISTINGS,
    ACTIONS.APPROVE_SERVICE_BOOKINGS,
    ACTIONS.VIEW_SERVICE_BOOKINGS,
    ACTIONS.VIEW_ANALYTICS,
    ACTIONS.MANAGE_PROFILE,
    // Optional: Service providers can also apply for jobs
    ACTIONS.BROWSE_JOBS,
    ACTIONS.APPLY_FOR_JOBS,
    ACTIONS.VIEW_JOB_APPLICATIONS
  ],
  
  // Employer Capabilities  
  [CAPABILITIES.CAN_POST_JOBS]: [
    ACTIONS.POST_JOBS,
    ACTIONS.MANAGE_JOB_POSTINGS,
    ACTIONS.VIEW_JOB_APPLICANTS,
    ACTIONS.APPROVE_JOB_APPLICATIONS,
    ACTIONS.VIEW_ANALYTICS,
    ACTIONS.MANAGE_PROFILE,
    // Optional: Employers can also apply for jobs
    ACTIONS.BROWSE_JOBS,
    ACTIONS.APPLY_FOR_JOBS,
    ACTIONS.VIEW_JOB_APPLICATIONS,
    // Optional: Employers can book services
    ACTIONS.BROWSE_SERVICES,
    ACTIONS.BOOK_SERVICES,
    ACTIONS.VIEW_SERVICE_BOOKINGS,
    // Optional: Employers can provide services
    ACTIONS.POST_SERVICES,
    ACTIONS.MANAGE_SERVICE_LISTINGS,
    ACTIONS.APPROVE_SERVICE_BOOKINGS
  ]
};

// Role-based capability defaults for backward compatibility
export const ROLE_CAPABILITY_DEFAULTS = {
  general: [CAPABILITIES.CAN_BOOK_SERVICES], // General users can only book services initially
  jobseeker: [CAPABILITIES.CAN_APPLY_FOR_JOBS, CAPABILITIES.CAN_BOOK_SERVICES],
  serviceprovider: [
    CAPABILITIES.CAN_APPLY_FOR_JOBS, 
    CAPABILITIES.CAN_BOOK_SERVICES, 
    CAPABILITIES.CAN_PROVIDE_SERVICES,
    CAPABILITIES.CAN_POST_JOBS // Optional
  ],
  employer: [
    CAPABILITIES.CAN_POST_JOBS,
    CAPABILITIES.CAN_APPLY_FOR_JOBS, // Optional
    CAPABILITIES.CAN_BOOK_SERVICES, // Optional  
    CAPABILITIES.CAN_PROVIDE_SERVICES // Optional
  ],
  admin: [
    CAPABILITIES.CAN_APPLY_FOR_JOBS,
    CAPABILITIES.CAN_BOOK_SERVICES,
    CAPABILITIES.CAN_PROVIDE_SERVICES,
    CAPABILITIES.CAN_POST_JOBS
  ]
};

// Utility functions for permission checking
export const hasCapability = (user, capability) => {
  // Super admin has unrestricted access (GodMode)
  if (user?.role === 'super_admin') return true;
  
  if (!user || !user.userCapabilities) {
    // Fallback to role-based check for backward compatibility
    if (user && user.userRole && ROLE_CAPABILITY_DEFAULTS[user.userRole]) {
      return ROLE_CAPABILITY_DEFAULTS[user.userRole].includes(capability);
    }
    return false;
  }
  return user.userCapabilities.includes(capability);
};

export const canPerformAction = (user, action) => {
  // Super admin has unrestricted access (GodMode)
  if (user?.role === 'super_admin') return true;
  
  if (!user) return false;
  
  // Get user's capabilities
  let userCapabilities = [];
  if (user.userCapabilities && Array.isArray(user.userCapabilities)) {
    userCapabilities = user.userCapabilities;
  } else if (user.userRole && ROLE_CAPABILITY_DEFAULTS[user.userRole]) {
    // Fallback to role-based capabilities
    userCapabilities = ROLE_CAPABILITY_DEFAULTS[user.userRole];
  }
  
  // Check if any of the user's capabilities allow this action
  return userCapabilities.some(capability => {
    const allowedActions = PERMISSIONS_MATRIX[capability] || [];
    return allowedActions.includes(action);
  });
};

export const getUserAllowedActions = (user) => {
  if (!user) return [];
  
  let userCapabilities = [];
  if (user.userCapabilities && Array.isArray(user.userCapabilities)) {
    userCapabilities = user.userCapabilities;
  } else if (user.userRole && ROLE_CAPABILITY_DEFAULTS[user.userRole]) {
    userCapabilities = ROLE_CAPABILITY_DEFAULTS[user.userRole];
  }
  
  // Collect all allowed actions from all capabilities
  const allowedActions = new Set();
  userCapabilities.forEach(capability => {
    const actions = PERMISSIONS_MATRIX[capability] || [];
    actions.forEach(action => allowedActions.add(action));
  });
  
  return Array.from(allowedActions);
};

// Dashboard path utilities
export const getDashboardPath = (primaryRole) => {
  switch (primaryRole) {
    case 'admin':
      return '/admin';
    case 'employer':
      return '/employer';
    case 'jobseeker':
      return '/jobseeker';
    case 'serviceprovider':
      return '/service-provider';
    case 'general':
      return '/dashboard'; // Unified dashboard for general users
    default:
      return '/dashboard'; // Unified dashboard
  }
};

export const getProfilePath = (primaryRole) => {
  switch (primaryRole) {
    case 'admin':
      return '/admin/profile';
    case 'employer':
      return '/employer/profile';
    case 'jobseeker':
      return '/jobseeker/profile';
    case 'serviceprovider':
      return '/service-provider/profile';
    case 'general':
      return '/profile'; // General profile for general users
    default:
      return '/profile';
  }
};

// Capability management utilities
export const getCapabilityDisplayName = (capability) => {
  const displayNames = {
    [CAPABILITIES.CAN_APPLY_FOR_JOBS]: 'Apply for Jobs',
    [CAPABILITIES.CAN_BOOK_SERVICES]: 'Book Services',
    [CAPABILITIES.CAN_PROVIDE_SERVICES]: 'Provide Services',
    [CAPABILITIES.CAN_POST_JOBS]: 'Post Jobs'
  };
  return displayNames[capability] || capability;
};

export const getCapabilityDescription = (capability) => {
  const descriptions = {
    [CAPABILITIES.CAN_APPLY_FOR_JOBS]: 'Browse and apply for job opportunities',
    [CAPABILITIES.CAN_BOOK_SERVICES]: 'Find and book services from providers',
    [CAPABILITIES.CAN_PROVIDE_SERVICES]: 'Offer services and manage bookings',
    [CAPABILITIES.CAN_POST_JOBS]: 'Create job postings and manage applications'
  };
  return descriptions[capability] || '';
};

// Validation utilities
export const validateCapabilities = (capabilities) => {
  if (!Array.isArray(capabilities)) return false;
  return capabilities.every(cap => Object.values(CAPABILITIES).includes(cap));
};

export const getDefaultCapabilitiesForRole = (role) => {
  return ROLE_CAPABILITY_DEFAULTS[role] || [];
};

export const capabilityLabels = {
  job_seeker: {
    name: 'Job Seeker',
    description: 'Looking for jobs and career growth'
  },
  employer: {
    name: 'Employer',
    description: 'Hiring talent and managing recruitment'
  }
};

const permissions = {
  CAPABILITIES,
  ACTIONS,
  PERMISSIONS_MATRIX,
  ROLE_CAPABILITY_DEFAULTS,
  hasCapability,
  canPerformAction,
  getUserAllowedActions,
  getDashboardPath,
  getProfilePath,
  getCapabilityDisplayName,
  getCapabilityDescription,
  validateCapabilities,
  getDefaultCapabilitiesForRole
};

export default permissions;